import { useMemo } from 'react';

import { type AirActionTrackingLocation, useTrackDeletedItems } from '@air/analytics';
import { Tasks } from '@air/api';

import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { type BoardDeletionTask, type GetBaseMetadata } from '~/store/tasks/types';
import { useAdjustSideNavCachedParentDirectDescendantCounts } from '~/utils/mutateUtils/SideNavBoards';
import { useCreateTask } from '~/utils/taskUtils/useCreateTask';

export type CreateDeleteBoardTaskParams = GetBaseMetadata<BoardDeletionTask> & {
  trackLocation: AirActionTrackingLocation;
};

export const useCreateDeleteBoardTask = () => {
  const { createTask } = useCreateTask();
  const { currentWorkspace } = useCurrentWorkspace();
  const { trackDeletedItems } = useTrackDeletedItems();
  const { decrementSideNavCachedParentDirectDescendantCounts, incrementSideNavCachedParentDirectDescendantCounts } =
    useAdjustSideNavCachedParentDirectDescendantCounts();

  const workspaceId = currentWorkspace?.id;

  const createDeleteBoardTask = useMemo(() => {
    if (!workspaceId) {
      return undefined;
    }

    return async ({ boardIds = [], libraryId, trackLocation }: CreateDeleteBoardTaskParams) => {
      trackDeletedItems({
        item_ids: boardIds,
        item_total: boardIds.length,
        item_type: 'board',
        location: trackLocation,
      });

      // Rows hide immediately via the in-progress task filter; parent chevrons
      // read `directDescendantBoardCount`, which otherwise stays stale until complete.
      decrementSideNavCachedParentDirectDescendantCounts(boardIds);

      const result = await createTask({
        task: {
          metadata: {
            libraryId,
            boardIds,
          },
          type: 'BoardDeletion',
          workspaceId,
        },
        createTask: () =>
          Tasks.createTask({
            workspaceId,
            task: {
              type: 'ContentRemover',
              args: {
                boardIds,
              },
            },
          }),
      });

      if (result.error) {
        incrementSideNavCachedParentDirectDescendantCounts(boardIds);
      }

      return result;
    };
  }, [
    createTask,
    decrementSideNavCachedParentDirectDescendantCounts,
    incrementSideNavCachedParentDirectDescendantCounts,
    trackDeletedItems,
    workspaceId,
  ]);

  return {
    createDeleteBoardTask,
  };
};
